<template>
  <div class="mini-map-container">
    <!-- 顶部统计卡片：显示设备总数、在线、正常、报警、离线数量 -->
    <div class="stats-header">
      <div class="stat-item" @click="filterByStatus('all')">
        <span class="stat-label">总数</span>
        <span class="stat-value total">{{ statistics.total }}</span>
      </div>
      <div class="stat-item" @click="filterByStatus('online')">
        <span class="stat-label">在线</span>
        <span class="stat-value online">{{ statistics.online }}</span>
      </div>
      <div class="stat-item" @click="filterByStatus('normal')">
        <span class="stat-label">正常</span>
        <span class="stat-value normal">{{ statistics.normal }}</span>
      </div>
      <div class="stat-item" @click="filterByStatus('alarm')">
        <span class="stat-label">报警</span>
        <span class="stat-value alarm">{{ statistics.alarm }}</span>
      </div>
      <div class="stat-item" @click="filterByStatus('offline')">
        <span class="stat-label">离线</span>
        <span class="stat-value offline">{{ statistics.offline }}</span>
      </div>
    </div>

    <!-- 操作栏：用户筛选 + 省份筛选（两行） -->
    <div class="action-bar">
      <!-- 第一行：用户筛选（有权限时显示）+ 刷新 -->
      <div class="action-row">
        <!-- 有权限时显示用户筛选 -->
        <div v-if="hasManagePermission" class="action-btn" @click="showUserPicker = true">
          <span class="action-btn-text">{{ selectedUserName }}</span>
          <span class="action-btn-arrow">▼</span>
        </div>
        <!-- 无权限时显示省份筛选（第一行） -->
        <div v-else class="action-btn" @click="showProvincePicker = true">
          <span class="action-btn-text">{{ selectedProvinceName }}</span>
          <span class="action-btn-arrow">▼</span>
        </div>
        <div class="action-row-spacer"></div>
        <button class="refresh-btn" @click="refreshPage">刷新</button>
      </div>
      <!-- 第二行：省份筛选（有权限时才显示） -->
      <div v-if="hasManagePermission" class="action-row">
        <div class="action-btn" @click="showProvincePicker = true">
          <span class="action-btn-text">{{ selectedProvinceName }}</span>
          <span class="action-btn-arrow">▼</span>
        </div>
      </div>
    </div>

    <!-- 高德地图容器 -->
    <div id="mini-map" class="map-wrapper"></div>

    <!-- 右下角定位按钮 -->
    <div class="location-btn-container" @click="locateToMyPosition">
      <div class="location-btn" :class="{ 'locating': locating }">
        <i class="el-icon-location" :class="{ 'rotating': locating }"></i>
      </div>
    </div>

    <!-- 省份选择器（底部弹出滚动选择器） -->
    <div class="picker-overlay" v-if="showProvincePicker" @click="showProvincePicker = false">
      <div class="picker-container" @click.stop>
        <div class="picker-title-bar">
          <span class="picker-title">选择省份</span>
        </div>
        <div
          class="picker-scroll-wrap"
          ref="pickerScrollWrap"
          @wheel.prevent="handleWheel"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <div class="picker-mask picker-mask-top"></div>
          <div class="picker-mask picker-mask-bottom"></div>
          <div class="picker-indicator"></div>
          <div class="picker-scroll">
            <div class="picker-scroll-content" :style="{ transform: `translateY(${scrollOffset}px)` }">
              <div
                v-for="(p, index) in provinces"
                :key="p"
                class="picker-item"
                :class="{ 'picker-item-active': tempSelectedIndex === index }"
                @click="selectItem(index)"
              >
                {{ p }}
              </div>
            </div>
          </div>
        </div>
        <div class="picker-footer">
          <button class="picker-btn picker-btn-cancel" @click="showProvincePicker = false">取消</button>
          <button class="picker-btn picker-btn-confirm" @click="confirmProvince">确认</button>
        </div>
      </div>
    </div>

    <!-- 用户选择器（底部弹出滚动选择器） -->
    <div class="picker-overlay" v-if="showUserPicker" @click="showUserPicker = false">
      <div class="picker-container" @click.stop>
        <div class="picker-title-bar">
          <span class="picker-title">选择用户</span>
        </div>
        <div
          class="picker-scroll-wrap"
          ref="userPickerScrollWrap"
          @wheel.prevent="handleUserWheel"
          @touchstart="handleUserTouchStart"
          @touchmove="handleUserTouchMove"
          @touchend="handleUserTouchEnd"
        >
          <div class="picker-mask picker-mask-top"></div>
          <div class="picker-mask picker-mask-bottom"></div>
          <div class="picker-indicator"></div>
          <div class="picker-scroll">
            <div class="picker-scroll-content" :style="{ transform: `translateY(${userScrollOffset}px)` }">
              <div
                v-for="(user, index) in userOptions"
                :key="user.userAccount"
                class="picker-item"
                :class="{ 'picker-item-active': tempSelectedUserIndex === index }"
                @click="selectUserItem(index)"
              >
                {{ user.userAccount }}
              </div>
            </div>
          </div>
        </div>
        <div class="picker-footer">
          <button class="picker-btn picker-btn-cancel" @click="showUserPicker = false">取消</button>
          <button class="picker-btn picker-btn-confirm" @click="confirmUser">确认</button>
        </div>
      </div>
    </div>

    <!-- 设备详情弹窗 -->
    <el-dialog
      :visible.sync="showDevicePopup"
      width="85%"
      :show-close="false"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :modal="false"
      custom-class="device-dialog"
      @close="closePopup"
    >
      <div class="device-dialog-content" :class="getDeviceDialogClass(currentDevice.deviceStatus)">
        <div class="popup-header">
          <img :src="logoUrl" alt="Logo" class="popup-logo"/>
          <span class="popup-title">设备信息</span>
          <span class="popup-close" @click="closePopup">×</span>
        </div>
        <div class="popup-body">
          <div class="info-row" @click="copyText(currentDevice.deviceId || '未知', '设备ID')">
            <span class="label">设备ID：</span>
            <span class="value">{{ currentDevice.deviceId || '未知' }}</span>
          </div>
          <div class="info-row" @click="copyText(currentDevice.batchNo || '未知', '批次号')">
            <span class="label">批次号：</span>
            <span class="value">{{ currentDevice.batchNo || '未知' }}</span>
          </div>
          <div class="info-row" @click="copyText(currentDevice.province || '未知', '省份')">
            <span class="label">省份：</span>
            <span class="value">{{ currentDevice.province || '未知' }}</span>
          </div>
          <div class="info-row" @click="copyText(getStatusText(currentDevice.deviceStatus), '设备状态')">
            <span class="label">设备状态：</span>
            <span class="value" :class="getStatusClass(currentDevice.deviceStatus)">{{ getStatusText(currentDevice.deviceStatus) }}</span>
          </div>
          <div class="info-row" @click="copyText(getOnlineStatusText(currentDevice.onlineStatus), '联机状态')">
            <span class="label">联机状态：</span>
            <span class="value" :class="currentDevice.onlineStatus === '1' ? 'online-status-connected' : 'online-status-single'">
              {{ getOnlineStatusText(currentDevice.onlineStatus) }}
            </span>
          </div>
          <div class="info-row" @click="copyText(getPairStatusText(currentDevice), '配对状态')">
            <span class="label">配对状态：</span>
            <span class="value" :class="getPairStatusClass(currentDevice)">
              {{ getPairStatusText(currentDevice) }}
            </span>
          </div>
          <div class="info-row" @click="copyText(currentDevice.voltageLevel || '未知', '报警电压等级')">
            <span class="label">报警电压等级：</span>
            <span class="value alarm-value">{{ currentDevice.voltageLevel || '未知' }}</span>
          </div>
          <div class="info-row" @click="copyText(formatLocation(currentDevice), '位置')">
            <span class="label">位置：</span>
            <span class="value">{{ formatLocation(currentDevice) }}</span>
          </div>
          <div class="info-row" @click="copyText(formatTime(currentDevice.timestamp), '最后更新时间')">
            <span class="label">最后更新：</span>
            <span class="value">{{ formatTime(currentDevice.timestamp) }}</span>
          </div>
          <div v-if="currentDevice.deviceStatus === '2'" class="offline-tip">
            <span>设备已离线</span>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 聚合弹窗（点击聚合图标时弹出，显示该区域所有设备） -->
    <el-dialog
      :visible.sync="showClusterPopup"
      width="90%"
      :show-close="false"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :modal="false"
      custom-class="cluster-dialog"
      @close="closeClusterPopup"
    >
      <div class="cluster-dialog-content">
        <div class="popup-header">
          <img :src="logoUrl" alt="Logo" class="popup-logo"/>
          <span class="popup-title">此区域设备汇总</span>
          <span class="cluster-count">{{ clusterTotal }}台</span>
          <span class="popup-close" @click="closeClusterPopup">×</span>
        </div>
        <div class="popup-body">
          <div class="cluster-stats">
            <span class="stat-normal-badge">正常: {{ clusterStats.normal }}</span>
            <span class="stat-alarm-badge">报警: {{ clusterStats.alarm }}</span>
            <span class="stat-offline-badge">离线: {{ clusterStats.offline }}</span>
          </div>
          <div class="cluster-list">
            <div
              v-for="device in clusterDevices"
              :key="device.deviceId"
              class="cluster-item"
              :class="getClusterItemClass(device.deviceStatus)"
              @click="selectDeviceFromCluster(device)"
            >
              <div class="cluster-item-left">
                <span class="cluster-status-icon" :style="{ backgroundColor: getDotColor(device.deviceStatus) }"></span>
                <span class="cluster-item-id">{{ device.deviceId || '未知' }}</span>
              </div>
              <div class="cluster-item-right">
                <span class="cluster-item-time">{{ formatTime(device.timestamp) }}</span>
              </div>
            </div>
            <div v-if="clusterDevices.length === 0" class="cluster-empty">
              <span>附近暂无设备</span>
            </div>
          </div>
        </div>
        <div class="popup-footer" @click="closeClusterPopup">
          <span>点击设备查看详情</span>
        </div>
      </div>
    </el-dialog>

    <!-- 定位失败弹窗 -->
    <div class="location-fail-overlay" v-if="showLocationFailDialog" @click="closeLocationFailDialog">
      <div class="location-fail-dialog" @click.stop>
        <div class="location-fail-header">
          <span class="location-fail-title">定位提示</span>
          <span class="location-fail-close" @click="closeLocationFailDialog">×</span>
        </div>
        <div class="location-fail-body">
          <p class="location-fail-text">{{ locationFailMsg }}</p>
        </div>
        <div class="location-fail-footer">
          <button class="location-fail-btn" @click="closeLocationFailDialog">我知道了</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAllProvinces, getProvinceCenter } from '@/components/gps/provinceUtils'
import { wgs84ToGcj02 } from '@/components/gps/gpsCorrect'
import logoImg from '@/assets/login/max-logo.png'
import userPositionIcon from '@/assets/map/user-position.png'

export default {
  name: 'MiniMap',
  data() {
    const rawProvinces = getAllProvinces()
    const provincesList = rawProvinces.filter(p => p !== '全国')
    provincesList.unshift('全国')

    return {
      token: '',
      sessionId: '',
      userAccount: '', // 当前登录用户账号
      wsConnected: false,
      receiveCount: 0,
      ws: null,
      deviceMap: new Map(),
      deviceList: [],
      statistics: {
        total: 0,
        online: 0,
        normal: 0,
        alarm: 0,
        offline: 0
      },
      provinces: provincesList,
      selectedProvince: '',
      selectedProvinceName: '全国',
      statusFilter: 'all',

      // ===== 用户筛选相关 =====
      userOptions: [],
      selectedUserAccount: '',
      selectedUserName: '',
      hasManagePermission: false,
      userOptionsLoaded: false,

      // 地图相关
      map: null,
      heatmap: null,
      massMarks: null,
      infoWindow: null,
      mapLoaded: false,
      currentZoom: 3,
      isShowingMarkers: true,
      heatmapVisible: false,
      markerZoomThreshold: 8,
      heatmapZoomThreshold: 8,

      // 定时器
      refreshLoading: false,
      clearLoading: false,
      updateTimer: null,
      zoomTimer: null,

      // 定位相关
      locating: false,
      userMarker: null,
      userInfoWindow: null,

      // 弹窗相关
      showDevicePopup: false,
      showClusterPopup: false,
      currentDevice: {},
      clusterDevices: [],
      clusterStats: { normal: 0, alarm: 0, offline: 0 },
      clusterTotal: 0,
      logoUrl: logoImg,
      popupUpdateTimer: null,

      // 省份选择器
      showProvincePicker: false,
      tempSelectedIndex: 0,
      scrollOffset: 0,
      itemHeight: 44,
      maxScroll: 0,
      touchStartY: 0,
      touchStartOffset: 0,
      isTouching: false,
      containerHeight: 220,

      // 用户选择器
      showUserPicker: false,
      tempSelectedUserIndex: 0,
      userScrollOffset: 0,
      userTouchStartY: 0,
      userTouchStartOffset: 0,
      userIsTouching: false,
      userContainerHeight: 220,

      // 定位失败弹窗
      showLocationFailDialog: false,
      locationFailMsg: ''
    }
  },
  watch: {
    deviceMap: {
      deep: true,
      handler() {
        if (this.showDevicePopup && this.currentDevice.deviceId) {
          if (this.popupUpdateTimer) {
            clearTimeout(this.popupUpdateTimer)
          }
          this.popupUpdateTimer = setTimeout(() => {
            const updatedDevice = this.deviceMap.get(this.currentDevice.deviceId)
            if (updatedDevice) {
              this.currentDevice = { ...updatedDevice }
            }
          }, 50)
        }
      }
    },
    showDevicePopup(val) {
      if (!val) {
        setTimeout(() => {
          this.currentDevice = {}
        }, 300)
      }
    },
    // 省份选择器
    showProvincePicker(val) {
      if (val) {
        const index = this.provinces.indexOf(this.selectedProvinceName)
        this.tempSelectedIndex = index >= 0 ? index : 0
        this.$nextTick(() => {
          const wrapEl = this.$refs.pickerScrollWrap
          if (wrapEl) {
            this.containerHeight = wrapEl.offsetHeight
          }
          const centerOffset = this.getCenterOffset()
          this.scrollOffset = -this.tempSelectedIndex * this.itemHeight + centerOffset
          this.maxScroll = -(this.provinces.length - 1) * this.itemHeight + centerOffset
        })
        if (this.map) {
          this.map.setStatus({ touchZoom: false, dragEnable: false })
        }
      } else {
        if (this.map) {
          this.map.setStatus({ touchZoom: true, dragEnable: true })
        }
      }
    },
    // 用户选择器
    showUserPicker(val) {
      if (val) {
        // 找到当前选中用户在列表中的索引
        const index = this.userOptions.findIndex(u => u.userAccount === this.selectedUserAccount)
        this.tempSelectedUserIndex = index >= 0 ? index : 0
        this.$nextTick(() => {
          const wrapEl = this.$refs.userPickerScrollWrap
          if (wrapEl) {
            this.userContainerHeight = wrapEl.offsetHeight
          }
          const centerOffset = this.getUserCenterOffset()
          this.userScrollOffset = -this.tempSelectedUserIndex * this.itemHeight + centerOffset
        })
        if (this.map) {
          this.map.setStatus({ touchZoom: false, dragEnable: false })
        }
      } else {
        if (this.map) {
          this.map.setStatus({ touchZoom: true, dragEnable: true })
        }
      }
    }
  },

  mounted() {
    this.checkAndroidDevice()
    document.title = '高压预警监控'

    // 从 URL 获取 token 和 sessionId
    const urlParams = new URLSearchParams(window.location.search)
    let token = urlParams.get('token') || ''
    let sessionId = urlParams.get('sessionId') || ''

    // 兼容 hash 路由
    if (!token || !sessionId) {
      const hash = window.location.hash
      const queryIndex = hash.indexOf('?')
      if (queryIndex !== -1) {
        const queryString = hash.substring(queryIndex + 1)
        const params = new URLSearchParams(queryString)
        token = token || params.get('token') || ''
        sessionId = sessionId || params.get('sessionId') || ''
      }
    }

    this.token = token
    this.sessionId = sessionId || ''

    if (!this.token) {
      this.showLocationFailDialog = true
      return
    }

    // 加载用户列表
    this.loadUserOptions()

    // 初始化 WebSocket 和地图
    this.initWebSocket()
    this.loadAMap()
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy() {
    if (this.updateTimer) clearTimeout(this.updateTimer)
    if (this.zoomTimer) clearTimeout(this.zoomTimer)
    if (this.popupUpdateTimer) clearTimeout(this.popupUpdateTimer)
    window.removeEventListener('resize', this.handleResize)

    if (this.ws) this.ws.close()
    if (this.map) this.map.destroy()

    if (this.userMarker) {
      this.userMarker.setMap(null)
      this.userMarker = null
    }
    if (this.userInfoWindow) {
      this.userInfoWindow.close()
      this.userInfoWindow = null
    }
  },

  methods: {
    // ==================== 工具方法 ====================
    checkAndroidDevice() {
      const ua = navigator.userAgent.toLowerCase()
      const isAndroid = ua.indexOf('android') > -1 || ua.indexOf('adr') > -1
      if (isAndroid) {
        document.documentElement.classList.add('android-device')
      }
    },

    closeLocationFailDialog() {
      this.showLocationFailDialog = false
    },

    // ==================== 用户列表加载 ====================
    async loadUserOptions(keepSelected = false) {
      try {
        const token = this.token
        if (!token) return

        const previousSelectedUser = this.selectedUserAccount

        // 从 token 解析用户角色
        const userInfo = this.getUserInfoFromToken(token)
        this.userAccount = userInfo.sub || ''
        const roles = userInfo.roles || '[]'

        // 判断是否有管理权限（角色包含 "0" 超级管理员 或 "1" 管理员）
        const roleList = JSON.parse(roles)
        const isSuperAdmin = roleList.includes('0')
        const isAdmin = roleList.includes('1')
        const hasManagePermission = isSuperAdmin || isAdmin

        this.hasManagePermission = hasManagePermission

        // 只有有管理权限才加载用户列表
        if (!hasManagePermission) {
          this.userOptions = [{ userAccount: this.userAccount }]
          if (!keepSelected) {
            this.selectedUserAccount = this.userAccount
            this.selectedUserName = this.userAccount
          }
          this.userOptionsLoaded = true
          return
        }

        // ========== 调用接口获取用户列表（携带 Token） ==========
        const isLocal = window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost'
        const baseURL = isLocal ? 'http://127.0.0.1:10001' : 'https://gyfcdyjq.cn'
        // 注意：本地不加 /api，生产加 /api
        const apiPath = isLocal ? '/user/childAccounts' : '/api/user/childAccounts'
        const response = await fetch(baseURL + apiPath, {
          method: 'GET',
          headers: {
            'Authorization': 'Bearer ' + token.replace('Bearer ', '')
          }
        })
        const result = await response.json()

        if (result.status === 200) {
          const records = result.data.records || []

          // 后端已做好排序（当前用户排第一），直接使用
          this.userOptions = records.map(user => ({
            userId: user.userId,
            userAccount: user.userAccount
          }))

          // 如果需要保留当前选中的用户
          if (keepSelected && previousSelectedUser) {
            // 检查之前选中的用户是否还在列表中
            const exists = this.userOptions.some(u => u.userAccount === previousSelectedUser)
            if (exists) {
              // 存在则恢复
              this.selectedUserAccount = previousSelectedUser
              this.selectedUserName = previousSelectedUser
            } else if (this.userOptions.length > 0) {
              // 不存在则选中第一个
              this.selectedUserAccount = this.userOptions[0].userAccount
              this.selectedUserName = this.userOptions[0].userAccount
            }
          } else {
            // 不保留时选中第一个用户（当前用户）
            if (this.userOptions.length > 0) {
              this.selectedUserAccount = this.userOptions[0].userAccount
              this.selectedUserName = this.userOptions[0].userAccount
            }
          }

          this.userOptionsLoaded = true
        } else {
          console.error('获取用户列表失败:', result.message)
          this.userOptions = [{ userAccount: this.userAccount }]
          if (!keepSelected) {
            this.selectedUserAccount = this.userAccount
            this.selectedUserName = this.userAccount
          }
          this.userOptionsLoaded = true
        }
      } catch (error) {
        console.error('加载用户选项失败:', error)
        // 失败时至少显示自己
        this.userOptions = [{ userAccount: this.userAccount}]
        if (!keepSelected) {
          this.selectedUserAccount = this.userAccount
          this.selectedUserName = this.userAccount
        }
        this.hasManagePermission = true  // 调试用，生产环境根据角色判断
        this.userOptionsLoaded = true
      }
    },

    //从 token 解析用户信息
    getUserInfoFromToken(token) {
      try {
        const parts = token.split('.')
        if (parts.length === 3) {
          const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
          const binaryString = atob(base64)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          const decoded = new TextDecoder('utf-8').decode(bytes)
          return JSON.parse(decoded)
        }
        return {}
      } catch (e) {
        console.error('解析 Token 失败:', e)
        return {}
      }
    },

    // ==================== 省份选择器方法 ====================
    getCenterOffset() {
      return this.containerHeight / 2 - this.itemHeight / 2
    },

    getIndexFromOffset(offset) {
      const centerOffset = this.getCenterOffset()
      const index = Math.round((centerOffset - offset) / this.itemHeight)
      const totalItems = this.provinces.length
      return Math.max(0, Math.min(index, totalItems - 1))
    },

    scrollToIndex(index) {
      const totalItems = this.provinces.length
      const maxIndex = totalItems - 1
      const targetIndex = Math.max(0, Math.min(index, maxIndex))
      this.tempSelectedIndex = targetIndex
      this.$nextTick(() => {
        const centerOffset = this.getCenterOffset()
        this.scrollOffset = -targetIndex * this.itemHeight + centerOffset
        this.maxScroll = -(totalItems - 1) * this.itemHeight + centerOffset
      })
    },

    snapToNearest() {
      const totalItems = this.provinces.length
      let index = this.getIndexFromOffset(this.scrollOffset)
      index = Math.max(0, Math.min(index, totalItems - 1))
      this.tempSelectedIndex = index
      const centerOffset = this.getCenterOffset()
      this.scrollOffset = -index * this.itemHeight + centerOffset
    },

    handleWheel(e) {
      e.preventDefault()
      const delta = e.deltaY > 0 ? 1 : -1
      const newIndex = this.tempSelectedIndex + delta
      const totalItems = this.provinces.length
      const clampedIndex = Math.max(0, Math.min(newIndex, totalItems - 1))
      this.tempSelectedIndex = clampedIndex
      this.$nextTick(() => {
        const centerOffset = this.getCenterOffset()
        this.scrollOffset = -clampedIndex * this.itemHeight + centerOffset
      })
    },

    handleTouchStart(e) {
      this.isTouching = true
      this.touchStartY = e.touches[0].clientY
      this.touchStartOffset = this.scrollOffset
    },

    handleTouchMove(e) {
      if (!this.isTouching) return
      e.preventDefault()
      const deltaY = e.touches[0].clientY - this.touchStartY
      let newOffset = this.touchStartOffset + deltaY

      const maxOffset = this.getCenterOffset()
      const minOffset = -(this.provinces.length - 1) * this.itemHeight + this.getCenterOffset()
      if (newOffset > maxOffset) {
        newOffset = maxOffset + (newOffset - maxOffset) * 0.2
      } else if (newOffset < minOffset) {
        newOffset = minOffset + (newOffset - minOffset) * 0.2
      }
      this.scrollOffset = newOffset
    },

    handleTouchEnd(e) {
      this.isTouching = false
      this.snapToNearest()
    },

    selectItem(index) {
      const totalItems = this.provinces.length
      const clampedIndex = Math.max(0, Math.min(index, totalItems - 1))
      this.tempSelectedIndex = clampedIndex
      this.$nextTick(() => {
        const centerOffset = this.getCenterOffset()
        this.scrollOffset = -clampedIndex * this.itemHeight + centerOffset
      })
    },

    confirmProvince() {
      const selectedProvince = this.provinces[this.tempSelectedIndex]
      if (selectedProvince) {
        this.selectedProvinceName = selectedProvince
        this.selectedProvince = selectedProvince === '全国' ? '' : selectedProvince
        this.showProvincePicker = false
        this.onProvinceChange()
      }
    },

    // ==================== 用户选择器方法 ====================
    getUserCenterOffset() {
      return this.userContainerHeight / 2 - this.itemHeight / 2
    },

    getUserIndexFromOffset(offset) {
      const centerOffset = this.getUserCenterOffset()
      const index = Math.round((centerOffset - offset) / this.itemHeight)
      const totalItems = this.userOptions.length
      return Math.max(0, Math.min(index, totalItems - 1))
    },

    scrollToUserIndex(index) {
      const totalItems = this.userOptions.length
      const maxIndex = totalItems - 1
      const targetIndex = Math.max(0, Math.min(index, maxIndex))
      this.tempSelectedUserIndex = targetIndex
      this.$nextTick(() => {
        const centerOffset = this.getUserCenterOffset()
        this.userScrollOffset = -targetIndex * this.itemHeight + centerOffset
      })
    },

    snapToNearestUser() {
      const totalItems = this.userOptions.length
      let index = this.getUserIndexFromOffset(this.userScrollOffset)
      index = Math.max(0, Math.min(index, totalItems - 1))
      this.tempSelectedUserIndex = index
      const centerOffset = this.getUserCenterOffset()
      this.userScrollOffset = -index * this.itemHeight + centerOffset
    },

    handleUserWheel(e) {
      e.preventDefault()
      const delta = e.deltaY > 0 ? 1 : -1
      const newIndex = this.tempSelectedUserIndex + delta
      const totalItems = this.userOptions.length
      const clampedIndex = Math.max(0, Math.min(newIndex, totalItems - 1))
      this.tempSelectedUserIndex = clampedIndex
      this.$nextTick(() => {
        const centerOffset = this.getUserCenterOffset()
        this.userScrollOffset = -clampedIndex * this.itemHeight + centerOffset
      })
    },

    handleUserTouchStart(e) {
      this.userIsTouching = true
      this.userTouchStartY = e.touches[0].clientY
      this.userTouchStartOffset = this.userScrollOffset
    },

    handleUserTouchMove(e) {
      if (!this.userIsTouching) return
      e.preventDefault()
      const deltaY = e.touches[0].clientY - this.userTouchStartY
      let newOffset = this.userTouchStartOffset + deltaY

      const maxOffset = this.getUserCenterOffset()
      const minOffset = -(this.userOptions.length - 1) * this.itemHeight + this.getUserCenterOffset()
      if (newOffset > maxOffset) {
        newOffset = maxOffset + (newOffset - maxOffset) * 0.2
      } else if (newOffset < minOffset) {
        newOffset = minOffset + (newOffset - minOffset) * 0.2
      }
      this.userScrollOffset = newOffset
    },

    handleUserTouchEnd(e) {
      this.userIsTouching = false
      this.snapToNearestUser()
    },

    selectUserItem(index) {
      const totalItems = this.userOptions.length
      const clampedIndex = Math.max(0, Math.min(index, totalItems - 1))
      this.tempSelectedUserIndex = clampedIndex
      this.$nextTick(() => {
        const centerOffset = this.getUserCenterOffset()
        this.userScrollOffset = -clampedIndex * this.itemHeight + centerOffset
      })
    },

    confirmUser() {
      const selected = this.userOptions[this.tempSelectedUserIndex]
      if (selected) {
        const userAccount = selected.userAccount
        this.selectedUserAccount = userAccount
        this.selectedUserName = userAccount
        this.showUserPicker = false
        // 发送 user_filter，告诉后端切换用户了
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.sendUserFilter(this.selectedUserAccount || '')
        }
      }
      this.refreshDataOnly()
    },

    // ==================== WebSocket 连接 ====================
    initWebSocket() {
      const token = this.token
      if (!token) {
        this.showLocationFailDialog = true
        return
      }

      const tokenValue = token.replace('Bearer ', '')
      let wsUrl = `wss://gyfcdyjq.cn/wss/mobile?token=${tokenValue}`
      if (this.sessionId) {
        wsUrl += `&sessionId=${this.sessionId}&clientType=h5`
      }

      if (this.ws) {
        try {
          this.ws.close()
        } catch (e) {}
        this.ws = null
      }

      this.ws = new WebSocket(wsUrl)

      this.ws.onopen = () => {
        this.wsConnected = true
        console.log('WebSocket 连接成功')
        this.sendUserFilter(this.selectedUserAccount || '')
      }

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          this.handleWebSocketMessage(data)
        } catch (_) {}
      }

      this.ws.onclose = () => {
        this.wsConnected = false
        console.log('WebSocket 连接已关闭')
      }

      this.ws.onerror = (error) => {
        this.wsConnected = false
        console.error('WebSocket 连接错误:', error)
      }
    },

    /**
     * 发送用户筛选消息给后端
     * @param {string} selectedUserAccount - 选中的用户账号，空字符串表示当前用户
     */
    sendUserFilter(selectedUserAccount) {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          type: 'user_filter',
          selectedUserAccount: selectedUserAccount || ''
        }))
      }
    },

    // ==================== WebSocket 消息处理 ====================
    handleWebSocketMessage(data) {
      // 用户绑定批次号变更通知
      if (data.type === 'user_bind_change') {
        this.clearAllDeviceData()
        return
      }

      // 设备缓存清除通知
      if (data.type === 'cache_cleared') {
        this.clearAllDeviceData()
        return
      }

      // 全量设备数据
      if (data.type === 'device_data' && data.dataType === 'all' && Array.isArray(data.data)) {
        for (let device of data.data) {
          this.processSingleDevice(device)
        }
        this.refreshAfterDataLoad()
        return
      }

      // 单设备数据
      if (data.deviceId) {
        this.processSingleDevice(data)
        this.refreshAfterDataLoad()
        return
      }

      if (data.data && data.data.deviceId) {
        this.processSingleDevice(data.data)
        this.refreshAfterDataLoad()
        return
      }
    },

    processSingleDevice(data) {
      if (!data.deviceId) return

      this.receiveCount++

      let lat = null, lng = null
      if (data.gps && data.gps.lat && data.gps.lng) {
        lat = parseFloat(data.gps.lat)
        lng = parseFloat(data.gps.lng)
      } else if (data.lat && data.lng) {
        lat = parseFloat(data.lat)
        lng = parseFloat(data.lng)
      } else if (data.latitude && data.longitude) {
        lat = parseFloat(data.latitude)
        lng = parseFloat(data.longitude)
      }

      if (lat !== null && !isNaN(lat) && lat !== 0) {
        lat = parseFloat(lat)
      } else {
        lat = null
      }
      if (lng !== null && !isNaN(lng) && lng !== 0) {
        lng = parseFloat(lng)
      } else {
        lng = null
      }

      const device = {
        deviceId: data.deviceId,
        batchNo: data.batchNo,
        currentvoltage: data.currentvoltage || data.current_voltage,
        voltageLevel: data.voltageLevel || data.voltage_level,
        deviceStatus: data.deviceStatus || data.device_status,
        onlineStatus: data.onlineStatus || data.online_status,
        pairStatus: data.pairStatus || data.pair_status,
        province: data.province,
        lat: lat,
        lng: lng,
        timestamp: data.timestamp || Date.now()
      }

      this.deviceMap.set(device.deviceId, device)

      if (this.showDevicePopup && this.currentDevice.deviceId === device.deviceId) {
        this.currentDevice = { ...device }
      }
    },

    refreshAfterDataLoad() {
      let devices = Array.from(this.deviceMap.values())

      if (this.selectedProvince && this.selectedProvince !== '') {
        devices = devices.filter(d => d.province === this.selectedProvince)
      }

      if (this.statusFilter !== 'all') {
        devices = devices.filter(d => {
          if (this.statusFilter === 'online') return d.deviceStatus !== '2'
          if (this.statusFilter === 'normal') return d.deviceStatus === '0'
          if (this.statusFilter === 'alarm') return d.deviceStatus === '1'
          if (this.statusFilter === 'offline') return d.deviceStatus === '2'
          return true
        })
      }

      devices.sort((a, b) => {
        const getStatusPriority = (status) => {
          if (status === '1') return 1
          if (status === '0') return 2
          if (status === '2') return 3
          return 4
        }
        const statusDiff = getStatusPriority(a.deviceStatus) - getStatusPriority(b.deviceStatus)
        if (statusDiff !== 0) return statusDiff

        const getOnlinePriority = (onlineStatus) => {
          if (onlineStatus === '1') return 1
          if (onlineStatus === '0') return 2
          return 3
        }
        const onlineDiff = getOnlinePriority(a.onlineStatus) - getOnlinePriority(b.onlineStatus)
        if (onlineDiff !== 0) return onlineDiff

        const getPairPriority = (row) => {
          const onlineStatus = row.onlineStatus
          const pairStatus = row.pairStatus
          if (onlineStatus === '1' && pairStatus === '0') return 1
          if (onlineStatus === '0' && pairStatus === '1') return 2
          if (onlineStatus === '0' && pairStatus === '0') return 3
          return 4
        }
        return getPairPriority(a) - getPairPriority(b)
      })

      this.deviceList = devices
      this.updateStatistics()
      this.scheduleMapUpdate()

      if (this.showDevicePopup && this.currentDevice.deviceId) {
        const updatedDevice = this.deviceMap.get(this.currentDevice.deviceId)
        if (updatedDevice) {
          this.currentDevice = { ...updatedDevice }
        }
      }
    },

    updateStatistics() {
      const devices = this.deviceList || []
      const normal = devices.filter(d => d.deviceStatus === '0').length
      const alarm = devices.filter(d => d.deviceStatus === '1').length
      const offline = devices.filter(d => d.deviceStatus === '2').length

      this.statistics = {
        total: devices.length,
        online: normal + alarm,
        normal: normal,
        alarm: alarm,
        offline: offline
      }
    },

    // ==================== 地图相关 ====================
    handleResize() {
      if (this.map && this.map.resize) this.map.resize()
    },

    loadAMap() {
      const self = this
      if (window.AMap) {
        this.$nextTick(() => {
          this.initMap()
        })
        return
      }
      const script = document.createElement('script')
      script.src = 'https://webapi.amap.com/maps?v=2.0&key=8bfc15b8bcad85218c79aa70777760cf&plugin=AMap.HeatMap,AMap.ToolBar,AMap.Scale'
      script.onload = () => {
        self.$nextTick(() => {
          self.initMap()
        })
      }
      document.head.appendChild(script)
    },

    initMap() {
      const container = document.getElementById('mini-map')
      if (!container) return

      this.map = new AMap.Map('mini-map', {
        center: [104.0, 38.0],
        zoom: 3,
        viewMode: '2D',
        resizeEnable: true,
        mapStyle: 'amap://styles/885f50411a296a25de9a732cd1624fd0',
        features: ['bg', 'road', 'building', 'point']
      })

      this.map.addControl(new AMap.ToolBar({ position: 'RB' }))
      this.map.addControl(new AMap.Scale())

      this.map.on('zoomend', () => {
        this.currentZoom = this.map.getZoom()
        this.handleZoomChange()
      })

      this.infoWindow = new AMap.InfoWindow({
        offset: new AMap.Pixel(0, -35),
        autoMove: true,
        closeWhenClickMap: true
      })

      this.userInfoWindow = new AMap.InfoWindow({
        content: '<div style="padding: 4px 8px; font-size: 12px; background: rgba(0,0,0,0.7); color: #fff; border-radius: 4px;">我的位置</div>',
        offset: new AMap.Pixel(0, -35),
        autoMove: false,
        closeWhenClickMap: true,
        isCustom: true
      })

      this.mapLoaded = true
      this.initHeatmap()
      this.currentZoom = this.map.getZoom()
      this.checkAndToggleMarkers()
      this.checkHeatmapVisibility()
    },

    initHeatmap() {
      if (!this.map || !this.mapLoaded) return
      this.heatmap = new AMap.HeatMap(this.map, {
        radius: 15,
        opacity: [0.2, 0.95],
        gradient: {
          0.0: '#90EE90',
          0.2: '#52C41A',
          0.4: '#FAAD14',
          0.6: '#FF7A45',
          0.8: '#F5222D',
          1.0: '#A8071A'
        },
        visible: false
      })
      this.checkHeatmapVisibility()
    },

    checkHeatmapVisibility() {
      if (!this.heatmap || !this.mapLoaded || !this.map || !this.map.getZoom) return
      try {
        const currentZoom = this.map.getZoom()
        const shouldShow = currentZoom < this.heatmapZoomThreshold
        const wasVisible = this.heatmapVisible

        this.heatmapVisible = shouldShow
        this.heatmap.setOptions({ visible: shouldShow })

        if (shouldShow) {
          this.updateHeatmap()
        } else if (wasVisible && !shouldShow) {
          this.heatmap.setDataSet({ data: [] })
        }
      } catch (e) {
        console.warn('检查热力图可见性失败:', e.message)
      }
    },

    checkAndToggleMarkers() {
      if (!this.map || !this.mapLoaded) return
      const currentZoom = this.map.getZoom()
      const shouldShow = currentZoom >= this.markerZoomThreshold
      if (shouldShow !== this.isShowingMarkers) {
        this.isShowingMarkers = shouldShow
        if (shouldShow) {
          if (this.massMarks) this.massMarks.setMap(this.map)
          this.updateUserMarkerVisibility()
        } else {
          if (this.massMarks) this.massMarks.setMap(null)
          if (this.userMarker) this.userMarker.setMap(null)
          if (this.infoWindow) this.infoWindow.close()
          if (this.userInfoWindow) this.userInfoWindow.close()
        }
      }
    },

    handleZoomChange() {
      if (this.zoomTimer) clearTimeout(this.zoomTimer)
      this.zoomTimer = setTimeout(() => {
        this.checkAndToggleMarkers()
        this.checkHeatmapVisibility()
        this.updateUserMarkerVisibility()
      }, 150)
    },

    updateUserMarkerVisibility() {
      if (!this.userMarker) return
      const shouldShow = this.currentZoom >= this.markerZoomThreshold
      if (shouldShow) {
        this.userMarker.setMap(this.map)
      } else {
        this.userMarker.setMap(null)
        if (this.userInfoWindow) {
          this.userInfoWindow.close()
        }
      }
    },

    // ==================== 地图标记更新 ====================
    scheduleMapUpdate() {
      if (this.updateTimer) clearTimeout(this.updateTimer)
      this.updateTimer = setTimeout(() => {
        if (this.mapLoaded) {
          this.updateHeatmap()
          this.initMassMarks()
        }
      }, 200)
    },

    updateHeatmap() {
      if (!this.heatmap || !this.mapLoaded) return

      try {
        if (!this.heatmapVisible) {
          if (this.heatmap.getOptions().visible === false) {
            this.heatmap.setDataSet({ data: [] })
          }
          return
        }

        const heatData = this.deviceList
          .filter(d => d.lat && d.lng && d.deviceStatus !== '2')
          .map(d => ({
            lng: parseFloat(d.lng),
            lat: parseFloat(d.lat),
            count: d.deviceStatus === '1' ? 2 : 1
          }))

        if (heatData.length === 0) {
          this.heatmap.setDataSet({ data: [] })
          return
        }

        this.heatmap.setDataSet({
          data: heatData,
          max: Math.max(2, Math.ceil(heatData.length / 5))
        })
      } catch (e) {
        console.warn('更新热力图失败:', e.message)
      }
    },

    calculateDistance(lat1, lng1, lat2, lng2) {
      const R = 6371000
      const dLat = (lat2 - lat1) * Math.PI / 180
      const dLng = (lng2 - lng1) * Math.PI / 180
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2)
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    },

    clusterDevicesByDistance(devices, distanceThreshold) {
      const clusters = []
      const used = new Set()

      for (let i = 0; i < devices.length; i++) {
        if (used.has(i)) continue

        const cluster = [devices[i]]
        used.add(i)

        for (let j = i + 1; j < devices.length; j++) {
          if (used.has(j)) continue

          const lat1 = devices[i].lat
          const lng1 = devices[i].lng
          const lat2 = devices[j].lat
          const lng2 = devices[j].lng

          const dist = this.calculateDistance(lat1, lng1, lat2, lng2)

          if (dist < distanceThreshold) {
            cluster.push(devices[j])
            used.add(j)
          }
        }
        clusters.push(cluster)
      }
      return clusters
    },

    initMassMarks() {
      if (!this.map || !this.mapLoaded) return
      if (!this.deviceList || this.deviceList.length === 0) return
      const validDevices = this.deviceList.filter(d => d.lat && d.lng)
      if (validDevices.length === 0) return

      if (this.massMarks) {
        this.massMarks.setMap(null)
        this.massMarks = null
      }

      const clusters = this.clusterDevicesByDistance(validDevices, 10)
      const massData = []

      clusters.forEach(cluster => {
        let centerLat = 0, centerLng = 0
        cluster.forEach(device => {
          centerLat += device.lat
          centerLng += device.lng
        })
        centerLat /= cluster.length
        centerLng /= cluster.length

        let styleIndex = 0
        const isCluster = cluster.length > 1
        if (isCluster) {
          styleIndex = 2
        } else {
          const device = cluster[0]
          const status = device.deviceStatus || '0'
          if (status === '1') {
            styleIndex = 4
          } else if (status === '0') {
            styleIndex = 3
          } else if (status === '2') {
            styleIndex = 0
          } else {
            styleIndex = 0
          }
        }

        massData.push({
          lnglat: [centerLng, centerLat],
          style: styleIndex,
          extData: {
            devices: cluster,
            isCluster: isCluster,
            deviceCount: cluster.length
          }
        })
      })

      const iconStyles = [
        {
          url: require('@/assets/map/off-line.png'),
          anchor: new AMap.Pixel(17.5, 35),
          size: new AMap.Size(35, 35)
        },
        null,
        {
          url: require('@/assets/map/aggregate.png'),
          anchor: new AMap.Pixel(17.5, 35),
          size: new AMap.Size(35, 35)
        },
        {
          url: require('@/assets/map/on-line.png'),
          anchor: new AMap.Pixel(17.5, 35),
          size: new AMap.Size(35, 35)
        },
        {
          url: require('@/assets/map/alarm.png'),
          anchor: new AMap.Pixel(17.5, 35),
          size: new AMap.Size(35, 35)
        }
      ]

      this.massMarks = new AMap.MassMarks(massData, {
        zIndex: 9999,
        style: iconStyles
      })

      this.massMarks.on('click', (e) => {
        const extData = e.data.extData
        if (extData.isCluster) {
          this.clusterDevices = extData.devices
          this.clusterTotal = extData.devices.length
          const normalCount = extData.devices.filter(d => d.deviceStatus === '0').length
          const alarmCount = extData.devices.filter(d => d.deviceStatus === '1').length
          const offlineCount = extData.devices.filter(d => d.deviceStatus === '2').length
          this.clusterStats = { normal: normalCount, alarm: alarmCount, offline: offlineCount }
          this.showClusterPopup = true
          this.showDevicePopup = false
        } else {
          this.currentDevice = extData.devices[0]
          this.showDevicePopup = true
          this.showClusterPopup = false
        }
      })

      if (this.isShowingMarkers) {
        this.massMarks.setMap(this.map)
      }
    },

    // ==================== 用户交互 ====================
    onProvinceChange() {
      this.refreshAfterDataLoad()
      if (!this.map) return

      if (this.selectedProvince && this.selectedProvince !== '') {
        const center = getProvinceCenter(this.selectedProvince)
        if (center && center.length === 2) {
          this.map.setCenter([center[0], center[1]])
          this.map.setZoom(7)
        }
      } else {
        this.map.setCenter([104.0, 38.0])
        this.map.setZoom(3)
        this.currentZoom = 3
      }
    },

    filterByStatus(type) {
      this.statusFilter = type
      this.refreshAfterDataLoad()
    },

    locateToMyPosition() {
      if (this.locating) return
      this.locating = true

      navigator.geolocation.getCurrentPosition(
        (position) => {
          try {
            const wgsLng = position.coords.longitude
            const wgsLat = position.coords.latitude
            const [gcjLat, gcjLng] = wgs84ToGcj02(wgsLat, wgsLng)

            this.map.setCenter([gcjLng, gcjLat])
            this.map.setZoom(18)

            if (this.userMarker) {
              this.userMarker.setMap(null)
              this.userMarker = null
            }
            if (this.userInfoWindow) {
              this.userInfoWindow.close()
            }

            const userIcon = new AMap.Icon({
              size: new AMap.Size(35, 35),
              image: userPositionIcon,
              imageSize: new AMap.Size(35, 35)
            })

            this.userMarker = new AMap.Marker({
              position: [gcjLng, gcjLat],
              icon: userIcon,
              offset: new AMap.Pixel(-17.5, -35),
              zIndex: 1000,
              title: '我的位置'
            })

            if (this.currentZoom >= this.markerZoomThreshold) {
              this.userMarker.setMap(this.map)
            } else {
              this.userMarker.setMap(null)
            }

            this.userMarker.on('click', () => {
              if (this.infoWindow) {
                this.infoWindow.close()
              }
              this.userInfoWindow.open(this.map, [gcjLng, gcjLat])
            })

            this.locating = false
          } catch (err) {
            console.error('[定位处理异常]', err)
            this.locationFailMsg = '定位处理异常，请稍后重试'
            this.showLocationFailDialog = true
            this.locating = false
          }
        },
        (error) => {
          this.locationFailMsg = '无法获取您的位置，请前往系统设置允许位置权限，并确保网络连接正常'
          this.showLocationFailDialog = true
          this.locating = false
          console.warn('[定位失败]', error.message)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      )
    },

// ==================== 刷新相关 ====================
    /**
     * 刷新页面 - 智能刷新
     * - WS 断开时：重新建立 WebSocket 连接 + 刷新数据
     * - WS 连接时：只刷新数据，不重连 WS
     */
    refreshPage() {
      // 1. 重新加载用户选项
      this.loadUserOptions(true)

      // 2. 判断 WS 连接状态
      const isWsConnected = this.ws && this.ws.readyState === WebSocket.OPEN

      if (!isWsConnected) {
        console.log('WebSocket 已断开，重新连接...')
        // WS 断开 → 重新建立连接
        if (this.ws) {
          try {
            this.ws.close()
          } catch (e) {}
          this.ws = null
        }
        this.wsConnected = false
        this.initWebSocket()
      }

      // 3. 刷新数据
      this.refreshDataOnly()
    },

    /**
     * 只刷新数据，不重新加载用户选项
     * 用于切换用户、清空用户筛选等场景
     */
    refreshDataOnly() {
      this.refreshLoading = true

      this.deviceMap.clear()
      this.deviceList = []
      this.receiveCount = 0
      this.statistics = { total: 0, online: 0, normal: 0, alarm: 0, offline: 0 }

      if (this.massMarks) {
        this.massMarks.setMap(null)
        this.massMarks = null
      }
      if (this.heatmap) {
        this.heatmap.setDataSet({ data: [] })
      }

      this.showClusterPopup = false
      this.showDevicePopup = false
      this.clusterDevices = []
      this.currentDevice = {}

      if (this.map && this.mapLoaded) {
        if (this.selectedProvince && this.selectedProvince !== '') {
          const center = getProvinceCenter(this.selectedProvince)
          if (center && center.length === 2) {
            this.map.setCenter([center[0], center[1]])
            this.map.setZoom(7)
          }
        } else {
          this.map.setCenter([104.0, 38.0])
          this.map.setZoom(3)
          this.currentZoom = 3
        }
      }
      setTimeout(() => {
        this.refreshLoading = false
      }, 500)
    },

    // ==================== 数据清理方法 ====================
    /**
     * 清空所有设备数据（用户绑定批次号变更通知、设备缓存清除通知））
     */
    clearAllDeviceData(keepMapState = false) {
      this.deviceMap.clear()
      this.deviceList = []
      this.statistics = { total: 0, online: 0, normal: 0, alarm: 0, offline: 0 }
      if (this.heatmap) {
        this.heatmap.setDataSet({ data: [] })
      }
      if (this.massMarks) {
        this.massMarks.setMap(null)
        this.massMarks = null
      }
      this.showClusterPopup = false
      this.showDevicePopup = false
      this.clusterDevices = []
      this.currentDevice = {}
      return
    },

    // ==================== 工具方法 ====================
    getStatusText(deviceStatus) {
      if (!deviceStatus) return '离线'
      switch (deviceStatus) {
        case '0': return '正常'
        case '1': return '报警'
        case '2': return '离线'
        default: return '离线'
      }
    },

    getOnlineStatusText(onlineStatus) {
      if (!onlineStatus) return '单机'
      switch (onlineStatus) {
        case '0': return '单机'
        case '1': return '联机'
        default: return '单机'
      }
    },

    getPairStatusText(row) {
      const onlineStatus = row.onlineStatus
      const pairStatus = row.pairStatus
      if (onlineStatus === '1' && pairStatus === '0') return '已配对'
      if (onlineStatus === '0' && pairStatus === '1') return '配对中'
      return '未配对'
    },

    getPairStatusClass(row) {
      const onlineStatus = row.onlineStatus
      const pairStatus = row.pairStatus
      if (onlineStatus === '1' && pairStatus === '0') return 'pair-status-paired'
      if (onlineStatus === '0' && pairStatus === '1') return 'pair-status-pairing'
      return 'pair-status-unpaired'
    },

    getDotColor(deviceStatus) {
      if (deviceStatus === '0') return '#67C23A'
      if (deviceStatus === '1') return '#F56C6C'
      return '#909399'
    },

    getDeviceDialogClass(deviceStatus) {
      if (deviceStatus === '0') return 'device-dialog-normal'
      if (deviceStatus === '1') return 'device-dialog-alarm'
      if (deviceStatus === '2') return 'device-dialog-offline'
      return ''
    },

    getStatusClass(deviceStatus) {
      if (deviceStatus === '0') return 'status-normal'
      if (deviceStatus === '1') return 'status-alarm'
      return 'status-offline'
    },

    getClusterItemClass(deviceStatus) {
      if (deviceStatus === '0') return 'cluster-item-normal'
      if (deviceStatus === '1') return 'cluster-item-alarm'
      if (deviceStatus === '2') return 'cluster-item-offline'
      return ''
    },

    formatLocation(device) {
      let lng = device.lng
      let lat = device.lat

      if (lng !== null && lng !== undefined && lng !== '') {
        lng = parseFloat(lng)
      } else {
        lng = null
      }
      if (lat !== null && lat !== undefined && lat !== '') {
        lat = parseFloat(lat)
      } else {
        lat = null
      }

      if (lng !== null && lat !== null && !isNaN(lng) && !isNaN(lat) && lng !== 0 && lat !== 0) {
        return `${Number(lng).toFixed(4)}, ${Number(lat).toFixed(4)}`
      }

      return '未知'
    },

    formatTime(timestamp) {
      if (!timestamp) return '未知'
      const date = new Date(timestamp)
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`
    },

    copyText(text, fieldName) {
      if (!text || text === '-' || text === '未知') {
        return
      }
      navigator.clipboard.writeText(text)
    },

    closePopup() {
      this.showDevicePopup = false
    },

    closeClusterPopup() {
      this.showClusterPopup = false
      this.clusterDevices = []
    },

    // 从聚合弹窗选择设备
    selectDeviceFromCluster(device) {
      this.showClusterPopup = false
      this.currentDevice = device
      this.showDevicePopup = true
    }
  }
}
</script>

<style scoped>
/* ===== 全局重置 ===== */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent !important;
  -webkit-touch-callout: none !important;
  -webkit-user-select: none !important;
  user-select: none !important;
  outline: none !important;
}

/* ===== 容器 ===== */
.mini-map-container {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
}

body, .mini-map-container, .stat-item {
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue', 'Microsoft YaHei', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ===== 地图容器 ===== */
.map-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: #b3cffe !important;
}

::v-deep .amap-marker,
::v-deep .amap-mass-marker,
::v-deep .mass-marker {
  z-index: 1000 !important;
}

/* ===== 统计卡片 ===== */
.stats-header {
  position: fixed;
  top: calc(env(safe-area-inset-top, 10px) + 6px);
  left: 8px;
  right: 8px;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 8px;
  display: flex;
  justify-content: space-around;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-height: 60px;
}

.stat-item {
  text-align: center;
  flex: 1;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent !important;
  outline: none !important;
  padding: 2px 0;
}

.stat-item:active {
  transform: scale(0.95);
  opacity: 0.7;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
  white-space: nowrap;
}

.stat-value {
  display: block;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.2;
}

.stat-value.total { color: #409eff; }
.stat-value.online { color: #67C23A; }
.stat-value.normal { color: #67C23A; }
.stat-value.alarm { color: #f56c6c; }
.stat-value.offline { color: #909399; }

/* ===== 操作栏 ===== */
.action-bar {
  position: fixed;
  top: calc(env(safe-area-inset-top, 10px) + 84px);
  left: 8px;
  right: 8px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.action-row-spacer {
  flex: 1;
}

/* 通用按钮样式 */
.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: none;
  border-radius: 20px;
  height: 32px;
  padding: 0 12px;
  font-size: 13px;
  color: #333;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  width: auto;
  min-width: 60px;
  -webkit-tap-highlight-color: transparent !important;
  outline: none !important;
  -webkit-user-select: none !important;
  user-select: none !important;
}

.action-btn:active {
  transform: scale(0.96);
  background: rgba(240, 240, 240, 0.95);
}

.action-btn-text {
  font-weight: 500;
  color: #333;
  white-space: nowrap;
}

.action-btn-arrow {
  font-size: 10px;
  color: #000000;
  flex-shrink: 0;
}

/* 刷新按钮 */
.refresh-btn {
  background: #FFFFFF !important;
  border: none;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 12px;
  color: #333 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  font-weight: 500;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  flex-shrink: 0;
  transition: all 0.2s ease;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent !important;
  outline: none !important;
  -webkit-user-select: none !important;
  user-select: none !important;
}

.refresh-btn:active {
  transform: scale(0.96);
  background: rgba(240, 240, 240, 0.95) !important;
}

/* ===== 定位按钮 ===== */
.location-btn-container {
  position: absolute;
  bottom: 120px;
  right: 10px;
  z-index: 100;
  cursor: pointer;
}

.location-btn {
  width: 44px;
  height: 44px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #409eff;
  transition: all 0.3s ease;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent !important;
  outline: none !important;
  -webkit-user-select: none !important;
  user-select: none !important;
}

.location-btn:hover {
  background: #f0f0f0;
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
}

.location-btn.locating {
  background: #409eff;
  color: white;
}

.location-btn:active {
  transform: scale(0.95);
}

.location-btn i {
  transition: transform 0.3s ease;
  pointer-events: none;
}

.location-btn i.rotating {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ===== 选择器通用样式 ===== */
.picker-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.picker-container {
  background: #ffffff;
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.picker-title-bar {
  padding: 16px 20px 12px 20px;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.picker-title {
  font-size: 17px;
  font-weight: 600;
  color: #333;
}

.picker-scroll-wrap {
  position: relative;
  height: 220px;
  overflow: hidden;
  flex-shrink: 0;
  touch-action: none;
  -webkit-overflow-scrolling: auto;
  overscroll-behavior: none;
  cursor: grab;
}

.picker-scroll-wrap:active {
  cursor: grabbing;
}

.picker-scroll {
  height: 100%;
  overflow: hidden;
  touch-action: none;
}

.picker-scroll-content {
  will-change: transform;
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
}

.picker-item {
  height: 44px;
  line-height: 44px;
  text-align: center;
  font-size: 16px;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: color 0.15s, font-size 0.15s;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  -webkit-touch-callout: none;
  touch-action: manipulation;
}

.picker-item-active {
  color: #333;
  font-size: 18px;
  font-weight: 600;
}

.picker-indicator {
  position: absolute;
  top: 50%;
  left: 16px;
  right: 16px;
  height: 44px;
  transform: translateY(-50%);
  border-top: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
  pointer-events: none;
  z-index: 3;
}

.picker-mask {
  position: absolute;
  left: 0;
  right: 0;
  height: 60px;
  pointer-events: none;
  z-index: 2;
}

.picker-mask-top {
  top: 0;
  background: linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0));
}

.picker-mask-bottom {
  bottom: 0;
  background: linear-gradient(to top, rgba(255,255,255,0.95), rgba(255,255,255,0));
}

.picker-footer {
  display: flex;
  gap: 12px;
  padding: 12px 16px 20px 16px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.picker-btn {
  flex: 1;
  height: 44px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  -webkit-tap-highlight-color: transparent !important;
  outline: none !important;
  -webkit-user-select: none !important;
  user-select: none !important;
}

.picker-btn:active {
  opacity: 0.7;
  transform: scale(0.97);
}

.picker-btn-cancel {
  background: #f5f5f5;
  color: #34c759;
}

.picker-btn-confirm {
  background: #34c759;
  color: #ffffff;
}

/* ===== 弹窗样式（复用原有样式） ===== */
::v-deep .device-dialog {
  border-radius: 16px !important;
  overflow: hidden !important;
  background: transparent !important;
  box-shadow: none !important;
  width: 85% !important;
  max-width: 340px !important;
}

::v-deep .device-dialog .el-dialog__header,
::v-deep .device-dialog .el-dialog__footer {
  display: none !important;
}

::v-deep .device-dialog .el-dialog__body {
  padding: 0 !important;
  background: transparent !important;
}

::v-deep .cluster-dialog {
  border-radius: 16px !important;
  overflow: hidden !important;
  background: transparent !important;
  box-shadow: none !important;
  width: 90% !important;
  max-width: 350px !important;
}

::v-deep .cluster-dialog .el-dialog__header,
::v-deep .cluster-dialog .el-dialog__footer {
  display: none !important;
}

::v-deep .cluster-dialog .el-dialog__body {
  padding: 0 !important;
  background: transparent !important;
}

.device-dialog-content {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.device-dialog-content.device-dialog-normal {
  background: #67C23A;
}

.device-dialog-content.device-dialog-alarm {
  background: #e36464;
}

.device-dialog-content.device-dialog-offline {
  background: #e0e0e0;
}

.device-dialog-content .popup-header {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  background: inherit;
}

.device-dialog-content .popup-logo {
  width: 50px;
  height: 30px;
  margin-right: 8px;
  transform: scale(1.5);
}

.device-dialog-content .popup-title {
  flex: 1;
  font-size: 16px;
  font-weight: bold;
  color: #ffffff;
}

.device-dialog-content .popup-close {
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
  cursor: pointer;
  color: #ffffff;
  padding: 0 5px;
  -webkit-tap-highlight-color: transparent !important;
  outline: none !important;
}

.device-dialog-content .popup-body {
  padding: 15px;
  background: #ffffff;
}

.device-dialog-content .info-row {
  display: flex;
  margin-bottom: 12px;
  font-size: 13px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent !important;
  outline: none !important;
}

.device-dialog-content .info-row:active {
  opacity: 0.6;
}

.device-dialog-content .info-row .label {
  width: 95px;
  color: #666;
  flex-shrink: 0;
}

.device-dialog-content .info-row .value {
  flex: 1;
  color: #333;
  word-break: break-all;
}

.device-dialog-content .info-row .value.status-normal {
  color: #67C23A;
  font-weight: 500;
}

.device-dialog-content .info-row .value.status-alarm {
  color: #F56C6C;
  font-weight: 500;
}

.device-dialog-content .info-row .value.status-offline {
  color: #909399;
}

.device-dialog-content .info-row .alarm-value {
  color: #F56C6C;
  font-weight: 500;
}

.device-dialog-content .offline-tip {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
  color: #909399;
  font-size: 12px;
  text-align: center;
}

.cluster-dialog-content {
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.cluster-dialog-content .popup-header {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  background: #409eff !important;
  color: #fff;
}

.cluster-dialog-content .popup-logo {
  width: 50px;
  height: 30px;
  margin-right: 8px;
  transform: scale(1.5);
}

.cluster-dialog-content .popup-title {
  flex: 1;
  font-size: 15px;
  font-weight: bold;
  color: #ffffff;
}

.cluster-dialog-content .cluster-count {
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  margin-right: 8px;
}

.cluster-dialog-content .popup-close {
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
  cursor: pointer;
  padding: 0 5px;
  color: #ffffff;
  -webkit-tap-highlight-color: transparent !important;
  outline: none !important;
}

.cluster-dialog-content .popup-body {
  background: #fff;
}

.cluster-dialog-content .cluster-stats {
  display: flex;
  justify-content: space-around;
  padding: 12px 15px;
  background: #f5f7fa;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}

.cluster-stats span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.stat-normal-badge::before {
  content: "";
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #67C23A;
  margin-right: 4px;
}

.stat-alarm-badge::before {
  content: "";
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #F56C6C;
  margin-right: 4px;
}

.stat-offline-badge::before {
  content: "";
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #909399;
  margin-right: 4px;
}

.cluster-dialog-content .cluster-list {
  max-height: 350px;
  overflow-y: auto;
}

.cluster-dialog-content .cluster-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.2s;
  -webkit-tap-highlight-color: transparent !important;
  outline: none !important;
}

.cluster-dialog-content .cluster-item:active {
  opacity: 0.6;
}

.cluster-dialog-content .cluster-item.cluster-item-alarm {
  background-color: rgba(245, 108, 108, 0.08);
}

.cluster-dialog-content .cluster-item.cluster-item-offline {
  background-color: rgba(144, 147, 153, 0.08);
}

.cluster-dialog-content .cluster-item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.cluster-dialog-content .cluster-status-icon {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cluster-dialog-content .cluster-item-id {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cluster-dialog-content .cluster-item-right {
  flex-shrink: 0;
  text-align: right;
  margin-left: 10px;
}

.cluster-dialog-content .cluster-item-time {
  font-size: 11px;
  color: #666;
  white-space: nowrap;
}

.cluster-dialog-content .cluster-empty {
  padding: 40px;
  text-align: center;
  color: #999;
}

.cluster-dialog-content .popup-footer {
  padding: 12px;
  text-align: center;
  font-size: 12px;
  color: #999;
  border-top: 1px solid #eee;
  background: #fafafa;
  cursor: pointer;
  transition: background 0.2s;
  -webkit-tap-highlight-color: transparent !important;
  outline: none !important;
}

.cluster-dialog-content .popup-footer:active {
  opacity: 0.6;
}

/* ===== 状态颜色 ===== */
.online-status-connected {
  color: #67C23A !important;
  font-weight: 500;
}

.online-status-single {
  color: #909399 !important;
}

.pair-status-paired {
  color: #67C23A !important;
  font-weight: 500;
}

.pair-status-pairing {
  color: #E6A23C !important;
  font-weight: 500;
}

.pair-status-unpaired {
  color: #909399 !important;
}

/* ===== 定位失败弹窗 ===== */
.location-fail-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.25s ease;
}

.location-fail-dialog {
  background: #ffffff;
  border-radius: 16px;
  width: 85%;
  max-width: 340px;
  overflow: hidden;
  animation: scaleIn 0.25s ease;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2);
}

@keyframes scaleIn {
  from {
    transform: scale(0.85);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.location-fail-header {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  background: #ffffff;
  border-bottom: 1px solid #e5e5e5;
}

.location-fail-title {
  flex: 1;
  font-size: 17px;
  font-weight: 600;
  color: #333333;
}

.location-fail-close {
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
  cursor: pointer;
  color: #999999;
  padding: 0 5px;
}

.location-fail-body {
  padding: 24px 20px 20px 20px;
  text-align: center;
  background: #ffffff;
}

.location-fail-text {
  font-size: 15px;
  color: #333333;
  line-height: 1.6;
  margin: 0;
}

.location-fail-footer {
  padding: 0 20px 20px 20px;
  background: #ffffff;
}

.location-fail-btn {
  width: 100%;
  height: 44px;
  background: #409eff;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
}

.location-fail-btn:active {
  opacity: 0.7;
}
</style>

<style>
/* ===== 全局滚动条隐藏 ===== */
* {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

/* ===== 安卓专用：彻底禁用点击高亮 ===== */
.android-device,
.android-device *,
.android-device *:before,
.android-device *:after {
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0) !important;
  -webkit-tap-highlight-color: transparent !important;
  tap-highlight-color: transparent !important;
  -webkit-touch-callout: none !important;
  -webkit-user-select: none !important;
  user-select: none !important;
  outline: none !important;
}

.android-device *:active,
.android-device *:focus,
.android-device *:hover {
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0) !important;
  -webkit-tap-highlight-color: transparent !important;
  tap-highlight-color: transparent !important;
  outline: none !important;
}

.android-device button,
.android-device a,
.android-device [onclick],
.android-device [ontouchstart],
.android-device .stat-item,
.android-device .action-btn,
.android-device .refresh-btn,
.android-device .location-btn,
.android-device .picker-item,
.android-device .popup-close,
.android-device .cluster-item,
.android-device [class*="btn"],
.android-device [class*="item"] {
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0) !important;
  -webkit-tap-highlight-color: transparent !important;
  tap-highlight-color: transparent !important;
  touch-action: manipulation !important;
}

@media screen and (-webkit-min-device-pixel-ratio: 0) {
  .android-device * {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0) !important;
    -webkit-tap-highlight-color: transparent !important;
  }
}
</style>
